// React, Next
import { useContext, useEffect, useState } from 'react'
import Image, { StaticImageData } from 'next/image'

// MUI
import { Box, Typography, IconButton } from '@mui/material'
import { ArrowBackIos, ArrowForwardIos } from '@mui/icons-material'

// Custom
import { GlobalContext } from 'context/GlobalContextProvider'
import Section from '@/components/sections/Section'

// Style
import breakpoints from 'assets/theme/breakpoints'
import themeColors from '@/assets/theme/colors'
import { getFontBody, getFontH3 } from '@/assets/theme/fonts'

type Props = {
  slides: {
    title: string
    description: string
    img: StaticImageData
  }[]
  marginBottom?: string
  marginTop?: string
  header?: string
  textSpacing?: string
}

export default function SlidesSectionModel(props: Props) {
  const { screenDimensions } = useContext(GlobalContext)

  const [current, setCurrent] = useState(0)
  const [mobileMode, setMobileMode] = useState(false)

  useEffect(() => {
    setMobileMode(screenDimensions.width <= breakpoints.desktopToMobile)
  }, [screenDimensions])

  const nextSlide = () => {
    setCurrent(prev => (prev + 1) % props.slides.length)
  }

  const prevSlide = () => {
    setCurrent(prev => (prev - 1 + props.slides.length) % props.slides.length)
  }

  const getGrid = () => {
    const w = screenDimensions.width
    if (w > breakpoints.desktopToMobile) return 'span 6'
    return 'span 12'
  }

  const getImgSize = () => {
    const w = screenDimensions.width

    if (w > breakpoints.desktopToMobile) {
      return {
        width: w / 3.5,
        height: 250
      }
    }
    return {
      width: '100%',
      height: w / 2.6
    }
  }

  const getPhotoHeight = () => {
    const w = screenDimensions.width
    if (w > breakpoints.desktopToMobile) return 400
    return 300
  }

  const getSection = () => (
    <Box
      sx={{
        margin: '0 auto',
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: themeColors.bg.lightEnd,
        borderRadius: 5,
        padding: mobileMode ? '1rem' : '3rem'
      }}
    >
      {props.header && (
        <Typography
          variant="h3"
          fontSize={getFontBody()}
          sx={{
            textAlign: 'left',
            color: themeColors.bg.start,
            fontWeight: 600
          }}
        >
          {props.header.toLocaleUpperCase()}
        </Typography>
      )}
      <Box
        sx={{
          display: 'flex',
          transition: 'transform 0.6s ease',
          transform: `translateX(-${current * 100}%)`
        }}
      >
        {props.slides.map((card, index) => (
          <Box
            key={index}
            sx={{
              flex: '0 0 100%',
              display: 'flex',
              flexDirection: mobileMode ? 'column' : 'row',
              alignItems: 'center',
              justifyContent: mobileMode ? 'center' : 'space-between',
              gap: 4,
              px: mobileMode ? 2 : 6
            }}
          >
            <Box
              gridColumn={getGrid()}
              sx={{
                paddingTop: '2rem',
                justifyContent: 'center'
              }}
            >
              <Typography
                variant="h1"
                component="h3"
                fontSize={getFontH3()}
                sx={{
                  textAlign: 'left',
                  lineHeight: !mobileMode ? 1.3 : 1.5,
                  color: themeColors.gray.darker,
                  fontWeight: 'bold',
                  marginBottom: props.textSpacing ?? 0
                }}
              >
                {card.title}
              </Typography>

              <Typography
                variant="body1"
                fontSize={getFontBody()}
                sx={{
                  color: themeColors.gray.darker,
                  textAlign: 'left',
                  lineHeight: !mobileMode ? 1.5 : 2
                }}
              >
                {card.description}
              </Typography>
            </Box>

            <Box
              gridColumn={getGrid()}
              sx={{
                justifyContent: !mobileMode ? 'left' : 'center',
                paddingTop: mobileMode ? '0rem' : '2rem',
                display: 'grid'
              }}
            >
              <Image
                alt={card.title}
                src={card.img}
                style={{
                  borderRadius: mobileMode ? 0 : '5px',
                  width: getImgSize().width,
                  height: getPhotoHeight(),
                  objectFit: 'contain',
                  objectPosition: '50% 50%'
                }}
              />
            </Box>
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 1,
          mt: 2
        }}
      >
        <IconButton onClick={prevSlide} size="small">
          <ArrowBackIos fontSize="small" />
        </IconButton>

        {props.slides.map((_, index) => (
          <Box
            key={index}
            onClick={() => setCurrent(index)}
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              bgcolor: current === index ? 'primary.main' : 'grey.400',
              cursor: 'pointer'
            }}
          />
        ))}

        <IconButton onClick={nextSlide} size="small">
          <ArrowForwardIos fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  )

  // *** MAIN RENDER ***
  return (
    <Section
      marginTop={props.marginTop}
      marginBottom={props.marginBottom}
      content={getSection()}
    />
  )
}
